import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUpRight, Check, Phone, Mail, ShieldCheck, Landmark, CalendarDays, Ban } from 'lucide-react';
import { Header, Footer } from '@/components/public';
import { SEOHead } from '@/components/seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { UsEnquiryModal } from '@/components/public/us/UsEnquiryModal';
import { UsSectionHeading, UsEnquireButton } from '@/components/public/us/UsElements';
import { SITE_ORIGIN } from '@/components/public/us/usContent';
import '@/components/public/uk/uk.css';

const PHONE = '+1 (205) 624-5819';
const PHONE_HREF = 'tel:+12056245819';
const EMAIL = 'hi@starteaseai.com';
const CANONICAL = `${SITE_ORIGIN}/usa/sales-tax/`;
const CTA = 'Request a consultation';

const HANDLE = [
  ['Nexus monitoring', 'We track your sales against each state’s economic-nexus thresholds and tell you before you cross one — so registration is proactive, not late.'],
  ['Registration', 'When you hit nexus, we handle the permit paperwork and set up your state account.'],
  ['Return preparation & filing', 'We reconcile your marketplace and channel sales data, then file active returns in every state where you’re registered.'],
  ['NIL returns', 'We file legally required zero-dollar returns so open permits stay compliant.'],
  ['Remittance', 'We prepare and submit the payment due to each state alongside the return.'],
  ['Notice handling', 'We monitor for and respond to state sales-tax notices on your behalf.'],
  ['Reconciliation reporting', 'Collected-vs-remitted reconciliation that ties back to your books.'],
  ['Cleanup', 'We fix historical filing, registration or reconciliation errors.'],
];

const PRICING = [
  ['Active state sales tax return', '$75', '/ state, per return', 'Reconciled from your channels and filed in every state where you’re registered.'],
  ['NIL / zero-dollar return', '$60', '/ state, per return', 'Legally required zero-dollar returns so open permits stay compliant.'],
  ['Sales tax registration', '$50', 'per state, one-time', 'Permit paperwork and state account setup when you hit nexus in a new state.'],
  ['Account cleanup', '$50', '/ hour', 'Fixing historical filing, registration or reconciliation errors. Pre-approval required beyond the first hour.'],
];

const WORKFLOW = [
  ['Nexus', 'We watch your sales against each state’s economic-nexus thresholds and flag before you cross.'],
  ['Registration', 'When you hit nexus, we file the permit paperwork and open the state account.'],
  ['Filing', 'We reconcile your channel sales and file active returns in every registered state.'],
  ['Remittance', 'We prepare and submit the payment due to each state with the return.'],
  ['Notices', 'We monitor for and respond to state sales-tax notices on your behalf.'],
  ['Reconciliation', 'Collected-vs-remitted reconciliation that ties back to your books.'],
];

const STEPS = [
  ['Discovery call', 'We review your channels, current registrations and where you have nexus.'],
  ['Secure setup', 'You share read access to your sales channels — Shopify, Amazon, Stripe and the rest — and we map your filing calendar. Nothing for you to install.'],
  ['Hands-free filing', 'We file, remit and handle notices every period, under the Tax Filing Guarantee.'],
];

const FAQS = [
  ['Do I log into anything?', 'No. This is a fully managed service — there’s no portal, dashboard or software for you to operate. A real team registers, files and remits your sales tax for you.'],
  ['How is this different from Zamp, Avalara or TaxJar?', 'Those are tools you operate — you still learn a dashboard and do the work. StartEase is a managed service: a person handles registration, filing, remittance and notices end to end, priced transparently per filing with no annual contract.'],
  ['What counts as nexus, and how do you track it?', 'Economic nexus is triggered when your sales into a state cross that state’s threshold (a dollar amount and/or transaction count). We monitor your channel sales against each state’s threshold and tell you before you cross one, so registration is proactive rather than late.'],
  ['What’s the difference between an active return and a NIL return?', 'An active return reports and remits tax you collected in a state ($75 per state, per return). A NIL return is a legally required zero-dollar return filed when a permit is open but no tax is due ($60 per state, per return), which keeps that permit compliant.'],
  ['Who remits the actual tax, and whose money is it?', 'The sales tax is your money, collected from your customers and remitted to the state. We prepare and submit that payment on your behalf — it is never marked up and never routed through us. Our fee is only the flat per-filing management charge.'],
  ['Can you register me in a new state and clean up past filings?', 'Yes. We handle new-state registration ($50 per state, one-time) and account cleanup for historical filing, registration or reconciliation errors ($50 per hour, with pre-approval beyond the first hour).'],
  ['Do you cover marketplace-facilitator states and home-rule cities?', 'Yes. We account for marketplace-facilitator rules — where a marketplace like Amazon collects on your behalf — so you don’t over-report, and we handle home-rule city filings where they apply. We confirm the exact scope for your channels during onboarding.'],
];

const NAV = [
  ['handle', 'What we handle'],
  ['pricing', 'Pricing'],
  ['fees', 'How fees work'],
  ['workflow', 'Workflow'],
  ['guarantee', 'Guarantee'],
  ['onboarding', 'Onboarding'],
  ['questions', 'FAQs'],
];

const card = { border: '1px solid var(--uk-line)', background: 'var(--uk-panel)', borderRadius: 12, padding: '26px 24px' };

export default function USASalesTaxPage() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash === '#enquiry') setEnquiryOpen(true);
    else if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [location.hash]);

  const enquire = () => setEnquiryOpen(true);
  const changeOpen = (value) => { setEnquiryOpen(value); if (!value && location.hash === '#enquiry') navigate(location.pathname, { replace: true }); };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Managed Sales Tax Services',
    serviceType: 'Managed US sales tax compliance',
    description: 'Fully managed US sales tax — a real team registers, files and remits your sales tax across all 50 states, priced transparently per filing. No portal, dashboard or software.',
    url: CANONICAL,
    areaServed: 'US',
    provider: { '@type': 'Organization', name: 'StartEase Agent', url: SITE_ORIGIN, telephone: '+1-205-624-5819', email: EMAIL },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
      { '@type': 'ListItem', position: 2, name: 'United States', item: `${SITE_ORIGIN}/usa/` },
      { '@type': 'ListItem', position: 3, name: 'Managed Sales Tax', item: CANONICAL },
    ],
  };

  return (
    <div className="uk-page" data-testid="us-salestax-page">
      <SEOHead
        title="Managed Sales Tax Services (US) — Done-for-You Filing & Remittance | StartEase Agent"
        description="Fully managed US sales tax. A real team registers, files and remits your sales tax across all 50 states — no portal, no dashboard, no software. Transparent per-filing pricing."
        canonical={CANONICAL}
      >
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </SEOHead>
      <Header />

      <main>
        {/* 1. HERO */}
        <section className="uk-hero" data-testid="salestax-hero">
          <div className="uk-wrap" style={{ paddingBlock: '56px 64px' }}>
            <nav className="uk-breadcrumb" aria-label="Breadcrumb" data-testid="salestax-breadcrumb">
              <Link to="/" data-testid="salestax-breadcrumb-home">Home</Link><span>/</span>
              <Link to="/usa/" data-testid="salestax-breadcrumb-usa">United States</Link><span>/</span>
              <span aria-current="page">Managed Sales Tax</span>
            </nav>
            <div className="uk-hero-copy" style={{ maxWidth: 780 }}>
              <div className="uk-eyebrow">United States<span className="uk-sep" />Managed sales tax</div>
              <h1 data-testid="salestax-hero-title">Sales tax, <em>handled for you.</em></h1>
              <p data-testid="salestax-hero-description">
                A real team registers, files and remits your US sales tax across all 50 states. No portal to
                log into, no dashboard to learn, no software to buy — we own the outcome and you operate nothing,
                priced transparently per filing.
              </p>
              <div className="uk-hero-actions">
                <UsEnquireButton onClick={enquire} testId="salestax-hero-cta">{CTA}</UsEnquireButton>
                <a href="#pricing" className="uk-text-link" data-testid="salestax-hero-pricing">See pricing<ArrowDown size={16} /></a>
              </div>
              <span className="uk-hero-footnote" data-testid="salestax-hero-footnote">
                Certified QuickBooks ProAdvisors <span> / </span> All 50 states <span> / </span> Per-filing pricing
              </span>
            </div>
          </div>
        </section>

        {/* Trust facts */}
        <div className="uk-facts-band"><div className="uk-wrap uk-facts" data-testid="salestax-facts">
          {[[ShieldCheck, 'Nothing to operate', 'No portal, no dashboard, no software — a real team does the work.'],
            [Landmark, 'Registered, filed, remitted', 'Nexus, registration, returns, remittance and notices, handled for you.'],
            [CalendarDays, 'Per-filing pricing', 'Pay per return — no annual lock-in and no surprise fees.']].map(([Icon, title, text], i) => (
            <div key={title} data-testid={`salestax-fact-${i}`}><Icon size={23} strokeWidth={1.4} /><div><strong>{title}</strong><p>{text}</p></div></div>
          ))}
        </div></div>

        {/* Section nav */}
        <nav className="uk-wrap uk-section-nav" aria-label="On this page" data-testid="salestax-section-nav">
          {NAV.map(([id, text], i) => <a key={id} href={`#${id}`} data-testid={`salestax-nav-${id}`}><span>0{i + 1}</span>{text}</a>)}
        </nav>

        {/* 2. WHAT WE HANDLE */}
        <section id="handle" className="uk-section uk-wrap" data-testid="salestax-handle-section">
          <UsSectionHeading id="salestax-handle" number="01" eyebrow="The managed scope" title="A team handles all of it." text="Everything a sales-tax platform would ask you to operate — done for you by a real person." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '18px 40px', marginTop: 10 }} data-testid="salestax-handle-grid">
            {HANDLE.map(([title, text], i) => (
              <div key={title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBlock: 14, borderTop: '1px solid var(--uk-line)' }} data-testid={`salestax-handle-${i}`}>
                <Check size={18} style={{ color: 'var(--uk-green)', flexShrink: 0, marginTop: 4 }} />
                <div><strong style={{ display: 'block', color: '#fff', fontSize: 15, fontWeight: 600 }}>{title}</strong><p style={{ color: 'var(--uk-ink)', fontSize: 13.5, marginTop: 4 }}>{text}</p></div>
              </div>
            ))}
          </div>
          <div className="uk-regulatory-note" data-testid="salestax-not-offered">
            <Ban size={24} />
            <div>
              <strong>A managed service — not a tool.</strong>
              <p>There is no customer dashboard or portal, no tax-calculation API and no real-time rooftop rate engine. You log into nothing; a team does the work and we own the outcome.</p>
            </div>
          </div>
        </section>

        {/* 3. PRICING */}
        <section id="pricing" className="uk-soft-band"><div className="uk-wrap uk-section" data-testid="salestax-pricing-section">
          <div className="uk-heading-row">
            <UsSectionHeading id="salestax-pricing" number="02" eyebrow="Transparent, per filing" title="Pay per filing. No lock-in." text="These are StartEase management fees. The sales tax itself is your money, remitted to the state, and any state permit fee is paid by you." />
            <UsEnquireButton secondary onClick={enquire} testId="salestax-pricing-cta">{CTA}</UsEnquireButton>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 20, marginTop: 14 }} data-testid="salestax-prices">
            {PRICING.map(([title, price, unit, text], i) => (
              <div key={title} style={card} data-testid={`salestax-price-${i}`}>
                <h3 style={{ color: '#fff', fontSize: 18, minHeight: 48 }}>{title}</h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 8 }}>
                  <span style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 34, color: '#fff', lineHeight: 1 }}>{price}</span>
                  <span style={{ color: '#6ee7b7', fontSize: 13 }}>{unit}</span>
                </div>
                <p style={{ color: 'var(--uk-ink)', fontSize: 13, marginTop: 14 }}>{text}</p>
              </div>
            ))}
          </div>
          <p className="uk-fineprint" style={{ marginTop: 22 }} data-testid="salestax-pricing-note">
            Pay per filing — no bundled annual contract and no surprise fees. Coverage across all 50 states.
          </p>
        </div></section>

        {/* 4. HOW FEES WORK */}
        <section id="fees" className="uk-section uk-wrap" data-testid="salestax-fees-section">
          <UsSectionHeading id="salestax-fees" number="03" eyebrow="Two separate numbers" title="Our fee vs. the tax you remit." text="We keep our flat management fee cleanly separate from the tax and state fees you pay directly." />
          <div className="uk-responsibilities">
            <div>
              <span className="uk-eyebrow">StartEase<span className="uk-sep" />our management fee</span>
              <h3>You pay us</h3>
              <ul>
                {['A flat per-filing fee for each active return', 'A flat per-filing fee for each NIL return', 'A one-time registration fee for each new state', 'Hourly cleanup for fixing historical errors'].map((text, i) => (
                  <li key={text} data-testid={`salestax-fee-us-${i}`}><Check size={17} /><span>{text}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <span className="uk-eyebrow">Paid directly<span className="uk-sep" />tax & state fees</span>
              <h3>You pay at cost</h3>
              <ul>
                {['The sales tax you collected — remitted to the state, your money, never ours', 'Any state permit or registration fees set by the state', 'Never marked up and never routed through us', 'We prepare and submit it; you fund it from your account'].map((text, i) => (
                  <li key={text} data-testid={`salestax-fee-client-${i}`}><Check size={17} /><span>{text}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 5. THE MANAGED WORKFLOW */}
        <section id="workflow" className="uk-dark-band"><div className="uk-wrap uk-section" data-testid="salestax-workflow-section">
          <UsSectionHeading id="salestax-workflow" number="04" eyebrow="End to end" title="The managed workflow." text="From the first threshold to the final reconciliation — one team, every step." />
          <ol className="pay-steps" data-testid="salestax-workflow-steps">
            {WORKFLOW.map(([title, text], i) => (
              <li key={title} data-testid={`salestax-workflow-${i + 1}`}><span className="pay-step-num">0{i + 1}</span><h3>{title}</h3><p>{text}</p></li>
            ))}
          </ol>
        </div></section>

        {/* 6. TAX FILING GUARANTEE */}
        <section id="guarantee" className="uk-section uk-wrap" data-testid="salestax-guarantee-section">
          <UsSectionHeading id="salestax-guarantee" number="05" eyebrow="Our commitment" title="The Tax Filing Guarantee." />
          <div style={{ ...card, marginTop: 10, display: 'flex', gap: 20, alignItems: 'flex-start' }} data-testid="salestax-guarantee-card">
            <ShieldCheck size={30} style={{ color: 'var(--uk-green)', flexShrink: 0 }} strokeWidth={1.4} />
            <div>
              <p style={{ color: '#e2e8f0', fontSize: 15.5, lineHeight: 1.8 }}>
                Submit clean sales data at least <strong style={{ color: '#6ee7b7', fontWeight: 600 }}>5 business days</strong> before the deadline and the filing is fully covered by the flat fee.
              </p>
              <p style={{ color: 'var(--uk-ink)', fontSize: 13.5, lineHeight: 1.8, marginTop: 14 }}>
                Data submitted <strong style={{ color: '#fff', fontWeight: 600 }}>4 business days or fewer</strong> before the deadline requires an urgent manual override: a <strong style={{ color: '#fff', fontWeight: 600 }}>$50 rush fee</strong> applies and the guarantee is voided for that filing, so late state fees aren’t covered.
              </p>
            </div>
          </div>
        </section>

        {/* 7. ONBOARDING */}
        <section id="onboarding" className="uk-soft-band"><div className="uk-wrap uk-section" data-testid="salestax-onboarding-section">
          <UsSectionHeading id="salestax-onboarding" number="06" eyebrow="From sign-up to hands-free" title="Three steps to managed sales tax." />
          <ol className="pay-steps" data-testid="salestax-steps">
            {STEPS.map(([title, text], i) => (
              <li key={title} data-testid={`salestax-step-${i + 1}`}><span className="pay-step-num">0{i + 1}</span><h3>{title}</h3><p>{text}</p></li>
            ))}
          </ol>
        </div></section>

        {/* 8. FAQ */}
        <section id="questions" className="uk-section uk-wrap" data-testid="salestax-faq-section">
          <UsSectionHeading id="salestax-faq" number="07" eyebrow="Questions, answered" title="Sales tax, made clear." text="What e-commerce sellers and finance teams ask us most." />
          <Accordion type="single" collapsible className="uk-faqs" data-testid="salestax-faqs">
            {FAQS.map(([question, answer], i) => (
              <AccordionItem key={question} value={`question-${i}`} data-testid={`salestax-faq-item-${i}`}>
                <AccordionTrigger data-testid={`salestax-faq-trigger-${i}`}>{question}</AccordionTrigger>
                <AccordionContent data-testid={`salestax-faq-answer-${i}`}>{answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* 9. CLOSING CTA */}
        <section className="uk-section uk-wrap uk-closing" data-testid="salestax-closing-section">
          <div>
            <span className="uk-eyebrow">Managed sales tax, per filing.</span>
            <h2>Let’s take sales tax off your plate.</h2>
            <p>Tell us your channels and where you sell — we’ll map your nexus and filing calendar, then handle it.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 18, fontSize: 14 }}>
              <a href={PHONE_HREF} className="uk-text-link" data-testid="salestax-closing-phone"><Phone size={15} />{PHONE}</a>
              <a href={`mailto:${EMAIL}`} className="uk-text-link" data-testid="salestax-closing-email"><Mail size={15} />{EMAIL}</a>
            </div>
          </div>
          <UsEnquireButton onClick={enquire} testId="salestax-closing-cta">{CTA}</UsEnquireButton>
        </section>

        <div className="uk-wrap uk-editorial-note" data-testid="salestax-editorial-note">
          <p>General information, not legal or tax advice. We file and remit on the client’s behalf as a managed service; we are not a law firm, and we do not provide a tax-calculation API or self-serve portal. Scope, inclusions and fees are confirmed in writing before engagement, and businesses remain responsible for their legal obligations.</p>
        </div>
      </main>

      <Footer />
      <UsEnquiryModal open={enquiryOpen} onOpenChange={changeOpen} structure="Existing US company support" />
    </div>
  );
}
